import { Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FormViewerComponent, SubmitFormEvent } from '@app/components/_public/form-viewer/form-viewer.component';
import { StepType } from '@app/forms/config/form-model';
import { ContratAccueilMentaloFormData, DemandeContratAccueilMentalo } from '@app/models/contrat-accueil';
import { FamilyService, FormConfigService, FormHelperService, PermissionService } from '@app/services';
import { Family } from '@app/models/family';
import { filter, forkJoin, Observable, of, switchMap, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-demande-contrat-accueil-edit',
  templateUrl: './demande-contrat-accueil-edit.component.html',
  styleUrl: './demande-contrat-accueil-edit.component.scss'
})
export class DemandeContratAccueilEditComponent implements OnInit {
  idContratAccueil: number;
  idFamille: number;
  readOnly: boolean = false;
  permToCreate: boolean;
  step: string;
  form: StepType[];
  data: any;
  form$: Observable<any>;
  isLoading = true;
  loadingMessage = 'Chargement';
  isNewContrat: boolean;
  formTitle: string;

  //Services
  route = inject(ActivatedRoute);
  permService = inject(PermissionService);
  familyService = inject(FamilyService);
  destroyRef: DestroyRef = inject(DestroyRef);
  formConfigService = inject(FormConfigService);
  helperService = inject(FormHelperService);
  location = inject(Location)

  @ViewChild(FormViewerComponent) formViewer: FormViewerComponent;

  ngOnInit(): void {
    this.idContratAccueil = +this.route.snapshot.paramMap.get('id') !== 0 ? +this.route.snapshot.paramMap.get('id') : null;
    this.step = this.route.snapshot.paramMap.get('step');
    this.isNewContrat = this.route.snapshot.routeConfig.path === "add";
    this.readOnly = !this.permService.hasPermission('demande_contrat_accueil_edit');
    this.permToCreate = this.permService.hasPermission('demande_contrat_accueil_create');

    if (this.permToCreate && !this.step) {
      this.readOnly = false;
    }

    this.formTitle = this.readOnly ? 'Aperçu de la demande de contrat d\'accueil' : 'Modification d\'une demande de contrat d\'accueil';

    if (this.isNewContrat) {
      this.formTitle = this.route.snapshot.data.title;
    }

    this.familyService.currentFamily$.pipe(
      filter(f => !!f),
      takeUntilDestroyed(this.destroyRef),
      tap(f => { this.idFamille = f.id; }),
      switchMap(family => this.familyService.getFormDataDemandeContratAccueil(family, this.idContratAccueil, this.step))
    ).subscribe((result: any) => {
      if (result.config) {
        this.form = this.formConfigService.getFormView(result.config).filter(f => f.enabled);
      }

      setTimeout(() => { // doit être encadré dans un setTimeout car sinon le 'formViewer' n'est pas accessible (car il était masqué par 'isLoading')...
        this.formViewer.handleReturnProgramOnDisplay(result)
      })

      this.data = !result.data ? { modeCreation: true } : result.data;

      if (result.decision) {
        this.readOnly = true;
      }

      if (this.form) {
        this.isLoading = false;
      }
    })
  }

  onSave(event: SubmitFormEvent) {
    const data: ContratAccueilMentaloFormData = event.model;
    this.formViewer.setErrorMessage('');
    this.loadingMessage = 'Enregistrement';

    if (this.readOnly) {
      return;
    }

    this.isLoading = true;
    const saveMethod = (this.idContratAccueil) ? this.familyService.updateDemandeContratAccueil(data, this.idFamille, this.idContratAccueil, this.step) : this.familyService.createDemandeContratAccueil(data, this.idFamille);

    saveMethod.subscribe((result: any) => {
      this.helperService.displayDebugTraces(result.traces);
      this.helperService.notifySuccess('Enregistrement réussi', result.messages);
      this.location.back();
    }, err => {
      this.isLoading = false;

      setTimeout(() => { // doit être encadré dans un setTimeout car sinon le 'formViewer' n'est pas accessible (car il était masqué par 'isLoading')...
        this.helperService.manageServerError(err, this.formViewer);
      })
    });
  }
}
